/**
 * ReservationChatAdminActions — 予約者チャット（自社チャット）のスタッフ操作
 *
 *   - 招待リンクを発行（resv_invite）: 電話番号もメールも無い予約（TR- など）のお客様に渡す 1回限り・期限付きのリンク。
 *     発行結果（URL・期限）をダイアログに出し、コピー／（LINE の会話から開いたときは）LINE 入力欄へ文面を入れる。
 *     自動送信はしない。
 *   - ロック解除（resv_unlock）: 下4桁を 5 回間違えて 30 分ロックされた予約を、確認ダイアログの後に解除する。
 *     ロック中かどうかはスタッフ向け API が無いので表示しない（resv_start は公開 API で IP 試行回数に数えるため呼ばない）。
 *
 * 置き場所: ReservationChatPanel のヘッダー（自社チャットを開いたとき）と、LineChatPanel の「紐付け済み予約」の各行
 * （LINE ユーザーに予約が紐付いているとき）。どちらも連絡コード／予約 ID が分かる場所。
 * 設計: md/master_md/page-line-chat.md「自社チャットの統合」／ booking-platform/md/customer-chat.md
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { isReservationChatEnabled } from '../config/backend';
import { issueReservationChatInvite, unlockReservationChat, type ReservationChatInvite } from '../hooks/useReservationChat';
import { buildInviteLineMessage, formatInviteExpiry } from '../lib/reservationChat';
import { logEvent } from '../lib/eventLog';
import { getInstanceConfig } from '../config/instances';

interface Props {
  /** 予約の連絡コード（招待リンクに必要）。無ければ招待ボタンは無効 */
  contactCode?: string | null;
  /** backend の予約 ID（ロック解除に必要）。無ければ解除ボタンは無効 */
  reservationId?: string | number | null;
  /** 文面を LINE の入力欄へ入れる（LINE の会話から開いたときだけ渡す。自動送信はしない） */
  onInsertToReply?: (text: string) => void;
  /** 文面の店舗名（省略可） */
  storeName?: string | null;
  /** アイコンだけ（紐付け一覧の行など狭い場所） */
  compact?: boolean;
}

const ReservationChatAdminActions: React.FC<Props> = ({ contactCode, reservationId, onInsertToReply, storeName, compact }) => {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [invite, setInvite] = useState<ReservationChatInvite | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'url' | 'message' | null>(null);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockResult, setUnlockResult] = useState<{ ok: boolean; text: string } | null>(null);

  if (!isReservationChatEnabled()) return null;

  const code = typeof contactCode === 'string' ? contactCode.trim() : '';
  const rid = reservationId != null && String(reservationId).trim() !== '' ? String(reservationId).trim() : '';

  const handleIssue = async () => {
    if (!code || inviting) return;
    setInviteOpen(true);
    setInviting(true);
    setInvite(null);
    setInviteError(null);
    setCopied(null);
    const r = await issueReservationChatInvite(code);
    setInviting(false);
    if ('invite' in r) {
      setInvite(r.invite);
    } else {
      setInviteError(`招待リンクを発行できませんでした: ${r.error}`);
    }
  };

  const copy = async (text: string, kind: 'url' | 'message') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
    } catch (e) {
      logEvent({ action: 'resv_chat.invite_copy', target: code, result: 'fail', err: e });
      setInviteError('コピーできませんでした。URL を選択して手動でコピーしてください');
    }
  };

  const handleUnlock = async () => {
    if (!rid || unlocking) return;
    const ok = window.confirm(
      `予約 #${rid}${code ? `（${code}）` : ''} の予約者チャットのロックを解除しますか？\n` +
        '下4桁を5回間違えると30分ロックされます。解除すると、お客様はすぐに下4桁で再確認できます。',
    );
    if (!ok) return;
    setUnlocking(true);
    setUnlockResult(null);
    const r = await unlockReservationChat(rid);
    setUnlocking(false);
    setUnlockResult(r.success ? { ok: true, text: 'ロックを解除しました' } : { ok: false, text: `ロック解除に失敗しました: ${r.error || '不明なエラー'}` });
  };

  // 店舗名は既定で現在の店舗（那覇/宮古）のブランド名を頭に付ける（2026-10-11）
  const message = invite ? buildInviteLineMessage(invite, storeName ?? getInstanceConfig().branding.storeName) : '';
  const expiry = invite ? formatInviteExpiry(invite.expires_at) : '';

  const inviteBtn = compact ? (
    <Tooltip title={code ? '招待リンクを発行（予約者チャット・1回限り）' : '連絡コードが無いので発行できません'}>
      <span>
        <IconButton size="small" onClick={handleIssue} disabled={!code || inviting} aria-label="招待リンクを発行">
          <LinkIcon sx={{ fontSize: 16, color: code ? '#0e7490' : undefined }} />
        </IconButton>
      </span>
    </Tooltip>
  ) : (
    <Tooltip title={code ? '電話番号もメールも無い予約のお客様に渡す、1回限り・期限付きのリンク' : '連絡コードが無いので発行できません'}>
      <span>
        <Button size="small" variant="outlined" startIcon={<LinkIcon />} onClick={handleIssue} disabled={!code || inviting} sx={{ textTransform: 'none', borderRadius: '8px', whiteSpace: 'nowrap' }}>
          招待リンクを発行
        </Button>
      </span>
    </Tooltip>
  );

  const unlockBtn = compact ? (
    <Tooltip title={rid ? '下4桁のロックを解除（予約者チャット）' : '予約 ID が無いので解除できません'}>
      <span>
        <IconButton size="small" onClick={handleUnlock} disabled={!rid || unlocking} aria-label="ロック解除">
          {unlocking ? <CircularProgress size={14} /> : <LockOpenIcon sx={{ fontSize: 16, color: rid ? '#b45309' : undefined }} />}
        </IconButton>
      </span>
    </Tooltip>
  ) : (
    <Tooltip title={rid ? '下4桁を5回間違えたロック（30分）を今すぐ解除する' : '予約 ID が無いので解除できません'}>
      <span>
        <Button size="small" variant="outlined" color="warning" startIcon={unlocking ? <CircularProgress size={14} /> : <LockOpenIcon />} onClick={handleUnlock} disabled={!rid || unlocking} sx={{ textTransform: 'none', borderRadius: '8px', whiteSpace: 'nowrap' }}>
          ロック解除
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {inviteBtn}
        {unlockBtn}
        {unlockResult && (
          <Typography variant="caption" sx={{ color: unlockResult.ok ? '#15803d' : '#b91c1c', fontWeight: 700 }}>
            {unlockResult.text}
          </Typography>
        )}
      </Box>

      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '16px' }}>予約者チャットの招待リンク{code ? ` — ${code}` : ''}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {inviting && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#64748b', fontSize: '13px' }}>
              <CircularProgress size={18} /> 発行中...
            </Box>
          )}
          {inviteError && <Typography sx={{ color: '#b91c1c', fontSize: '13px' }}>{inviteError}</Typography>}
          {invite && (
            <>
              <Box sx={{ p: 1.5, borderRadius: '8px', bgcolor: '#fffbeb', border: '1px solid #fde68a', fontSize: '13px', color: '#92400e', fontWeight: 700 }}>
                このリンクは <u>1回限り</u>・<u>{expiry || '期限付き'}</u> 有効です。
                開いた端末がそのまま予約者チャットを使えるようになるので、ご本人にだけ渡してください。
              </Box>
              <Box
                component="code"
                sx={{ display: 'block', p: 1.5, borderRadius: '8px', bgcolor: '#f1f5f9', fontSize: '12px', wordBreak: 'break-all', userSelect: 'all' }}
                data-testid="invite-url"
              >
                {invite.url}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" variant="contained" startIcon={<ContentCopyIcon />} onClick={() => copy(invite.url, 'url')} sx={{ textTransform: 'none', borderRadius: '8px' }}>
                  {copied === 'url' ? 'コピーしました' : 'URL をコピー'}
                </Button>
                <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => copy(message, 'message')} sx={{ textTransform: 'none', borderRadius: '8px' }}>
                  {copied === 'message' ? 'コピーしました' : '案内文つきでコピー'}
                </Button>
                {onInsertToReply && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    onClick={() => {
                      onInsertToReply(message);
                      setInviteOpen(false);
                    }}
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                  >
                    LINE の入力欄へ入れる（送信はしません）
                  </Button>
                )}
              </Box>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                期限を過ぎた・使われた後は、もう一度発行してください（前のリンクは無効のまま）。
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} sx={{ textTransform: 'none' }}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReservationChatAdminActions;
